import { Suspense, lazy, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { SearchSelect } from "../waba/MetricsTab";
import { inputStyle, shiftDay, todaySp } from "./common";

const Chart = lazy(() => import("./TaskMetricsCharts"));

interface ManagerRow { manager_id: string | null; name: string; open: number; overdue: number; finished: number; on_time_pct: number | null; avg_hours: number | null; postpones: number }
interface Metrics {
  kpis: { created: number; finished: number; open: number; overdue: number; on_time_pct: number | null; avg_hours: number | null; postpones: number };
  daily: { day: string; created: number; finished: number; backlog: number }[];
  by_manager: ManagerRow[];
  top_agents: { agent_id: string; name: string; created: number; open: number; finished: number }[];
  top_subjects: { subject: string; count: number }[];
  options: { agents: { id: string; name: string }[]; managers: { id: string; name: string }[]; subjects: string[] };
}

function fmtHours(h: number | null) {
  if (h == null) return "—";
  if (h < 1) return "<1h";
  return h >= 48 ? `${(h / 24).toFixed(1).replace(".", ",")} dias` : `${h.toFixed(1).replace(".", ",")}h`;
}

const fmtPct = (p: number | null) => (p == null ? "—" : `${p.toString().replace(".", ",")}%`);

export function TaskMetrics() {
  const [days, setDays] = useState(30);
  const [from, setFrom] = useState(shiftDay(todaySp(), -29));
  const [to, setTo] = useState(todaySp());
  const [managerId, setManagerId] = useState("");
  const [agentId, setAgentId] = useState("");
  const [subject, setSubject] = useState("");
  const [source, setSource] = useState("");

  const qs = new URLSearchParams({ from, to });
  if (managerId) qs.set("manager_id", managerId);
  if (agentId) qs.set("agent_id", agentId);
  if (subject) qs.set("subject", subject);
  if (source) qs.set("source", source);
  const { data: m, isLoading, isError } = useQuery({
    queryKey: ["tasks", "metrics", qs.toString()],
    queryFn: () => api.get<Metrics>(`/tasks/metrics?${qs}`),
  });

  function preset(n: number) {
    setDays(n);
    setTo(todaySp());
    setFrom(shiftDay(todaySp(), -(n - 1)));
  }

  const k = m?.kpis;
  const cards = k ? [
    { label: "Criadas", value: k.created },
    { label: "Finalizadas", value: k.finished, color: "var(--ok)" },
    { label: "Abertas agora", value: k.open },
    { label: "Atrasadas agora", value: k.overdue, color: k.overdue ? "var(--danger)" : undefined },
    { label: "% no prazo", value: fmtPct(k.on_time_pct) },
    { label: "Tempo médio", value: fmtHours(k.avg_hours), sub: "criação → finalização" },
    { label: "Adiamentos", value: k.postpones, color: k.postpones ? "var(--warn)" : undefined },
  ] : [];

  return (
    <div style={{ flex: 1, overflow: "auto", padding: 24, display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Filtros */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
        {[7, 30, 90].map(n => (
          <button key={n} onClick={() => preset(n)} style={{ padding: "5px 12px", borderRadius: 99, fontSize: 12, cursor: "pointer", background: days === n ? "var(--brand-soft)" : "var(--surface-hover)", border: `1px solid ${days === n ? "var(--brand)" : "var(--line)"}`, color: days === n ? "var(--brand)" : "var(--ink-secondary)" }}>
            {n} dias
          </button>
        ))}
        <input type="date" value={from} max={to} onChange={e => { setDays(0); setFrom(e.target.value); }} aria-label="De" style={{ ...inputStyle, width: 140, padding: "6px 8px", fontSize: 12 }} />
        <input type="date" value={to} min={from} onChange={e => { setDays(0); setTo(e.target.value); }} aria-label="Até" style={{ ...inputStyle, width: 140, padding: "6px 8px", fontSize: 12 }} />
        <SearchSelect value={managerId} onChange={setManagerId} options={(m?.options.managers ?? []).map(o => ({ value: o.id, label: o.name }))} placeholder="Todos os gestores" />
        <SearchSelect value={agentId} onChange={setAgentId} options={(m?.options.agents ?? []).map(o => ({ value: o.id, label: o.name }))} placeholder="Todos os agentes" />
        <SearchSelect value={subject} onChange={setSubject} options={(m?.options.subjects ?? []).map(s => ({ value: s, label: s }))} placeholder="Todos os assuntos" />
        <select value={source} onChange={e => setSource(e.target.value)} aria-label="Origem" style={{ ...inputStyle, width: "auto", padding: "6px 8px", fontSize: 12 }}>
          <option value="">Toda origem</option>
          <option value="manual">Manual</option>
          <option value="api">API</option>
        </select>
      </div>

      {isError && <span style={{ fontSize: 12, color: "var(--danger)" }}>Não foi possível carregar as métricas.</span>}
      {isLoading && <span style={{ fontSize: 12, color: "var(--ink-muted)" }}>Carregando…</span>}

      {m && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 12 }}>
            {cards.map(c => (
              <div key={c.label} className="metric-card" style={{ background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 10, padding: "16px 18px" }}>
                <div style={{ fontSize: 11, color: "var(--ink-muted)", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 10 }}>{c.label}</div>
                <div className="metric-value" style={{ fontSize: 26, fontWeight: 700, lineHeight: 1, color: c.color }}>{c.value}</div>
                {c.sub && <div style={{ fontSize: 11, color: "var(--ink-dim)", marginTop: 6 }}>{c.sub}</div>}
              </div>
            ))}
          </div>

          <Suspense fallback={<div style={{ fontSize: 12, color: "var(--ink-muted)" }}>Carregando gráficos…</div>}>
            <Panel title="Criadas x finalizadas por dia">
              <Chart type="daily" data={m.daily} />
            </Panel>

            <Panel title="Por gestor">
              {m.by_manager.length ? (
                <>
                  <Chart type="managers" data={m.by_manager.map(r => ({ name: r.name, overdue: r.overdue, open: r.open - r.overdue, finished: r.finished }))} />
                  <ManagerTable rows={m.by_manager} />
                </>
              ) : <Empty />}
            </Panel>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))", gap: 16 }}>
              <Panel title="Top agentes (criadas no período)">
                {m.top_agents.length ? <Chart type="top" data={m.top_agents.map(a => ({ name: a.name, value: a.created }))} label="Criadas" color="var(--brand)" /> : <Empty />}
              </Panel>
              <Panel title="Top assuntos">
                {m.top_subjects.length ? <Chart type="top" data={m.top_subjects.map(s => ({ name: s.subject, value: s.count }))} label="Tarefas" color="var(--info)" /> : <Empty />}
              </Panel>
            </div>
          </Suspense>
        </>
      )}
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ background: "var(--surface-card)", border: "1px solid var(--line)", borderRadius: 12, padding: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)", marginBottom: 12 }}>{title}</div>
      {children}
    </section>
  );
}

function Empty() {
  return <div style={{ fontSize: 12, color: "var(--ink-dim)", padding: "20px 0", textAlign: "center" }}>Sem dados no período.</div>;
}

function ManagerTable({ rows }: { rows: ManagerRow[] }) {
  const th: React.CSSProperties = { textAlign: "right", padding: "8px 10px", fontSize: 11, color: "var(--ink-muted)", fontWeight: 500, textTransform: "uppercase", letterSpacing: ".04em" };
  const td: React.CSSProperties = { textAlign: "right", padding: "8px 10px", fontSize: 12.5, color: "var(--ink)", borderTop: "1px solid var(--line)" };
  return (
    <div style={{ overflowX: "auto", marginTop: 12 }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={{ ...th, textAlign: "left" }}>Gestor</th>
            <th style={th}>Abertas</th>
            <th style={th}>Atrasadas</th>
            <th style={th}>Finalizadas</th>
            <th style={th}>% no prazo</th>
            <th style={th}>Tempo médio</th>
            <th style={th}>Adiamentos</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.manager_id ?? r.name}>
              <td style={{ ...td, textAlign: "left" }}>{r.name}</td>
              <td style={td}>{r.open}</td>
              <td style={{ ...td, color: r.overdue ? "var(--danger)" : td.color, fontWeight: r.overdue ? 600 : 400 }}>{r.overdue}</td>
              <td style={td}>{r.finished}</td>
              <td style={td}>{fmtPct(r.on_time_pct)}</td>
              <td style={td}>{fmtHours(r.avg_hours)}</td>
              <td style={{ ...td, color: r.postpones ? "var(--warn)" : td.color }}>{r.postpones}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
